import ApiService from './api.service';

// Mirrors MAX_ARTICLE_BODY_CHARS in src/models/knowledge_base_item.py — the API
// rejects article bodies over this size because long articles overload the AI
// assistant's context. See docs/knowledge_base_tools.md.
export const MAX_ARTICLE_BODY_CHARS = 20_000;

export type KnowledgeBaseItem = {
  id: string;
  item_type: 'folder' | 'article';
  name: string;
  parent_id: string | null;
  body?: string;
  tags: string[];
  status: 'active' | 'pending';
  proposed_by?: string | null;
  original_item_id?: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  last_updated: string;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_records?: number;
  total_pages?: number;
};

export class KnowledgeBaseService {
  async getTree(): Promise<ListRecordsResponse<KnowledgeBaseItem>> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/knowledge-base-items/tree'
    ) as ListRecordsResponse<KnowledgeBaseItem>;
    return result;
  }

  async findById(id: string): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/knowledge-base-items/${id}`
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async create(item: Partial<KnowledgeBaseItem>): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/knowledge-base-items',
      item
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async save(item: Partial<KnowledgeBaseItem>): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/knowledge-base-items/${item.id}`,
      item
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/knowledge-base-items/${id}`
    );
  }

  async move(id: string, newParentId: string | null): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/move`,
      { new_parent_id: newParentId }
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async search(query: string): Promise<ListRecordsResponse<KnowledgeBaseItem>> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/knowledge-base-items/search',
      { q: query }
    ) as ListRecordsResponse<KnowledgeBaseItem>;
    return result;
  }

  async getTags(): Promise<string[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/knowledge-base-items/tags'
    ) as { tags: string[] };
    return result.tags;
  }

  async approve(id: string): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/approve`
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async markPending(id: string): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/mark-pending`
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async reject(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/reject`
    );
  }
}

export const knowledgeBaseService = new KnowledgeBaseService();
